// packages/sdk/src/email-notifications.ts
// ---------------------------------------------------------------------------
// Email notification delivery history hook for the tenant app's
// Settings > Notifications > History view.
// ---------------------------------------------------------------------------

import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery } from '@tanstack/react-query';

export interface EmailDeliveryRow {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  email_queue_id: string | null;
  template: string;
  subject: string;
  recipient_email: string;
  resend_message_id: string | null;
  status: 'queued' | 'sent' | 'delivered' | 'bounced' | 'failed';
  bounced_reason: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
}

/**
 * Fetches the last 50 email delivery records for the given user within a
 * specific tenant. Results are ordered newest-first.
 *
 * RLS policy `member_read_own_deliveries` ensures users can only read their
 * own rows, so the userId filter is a performance hint, not a security gate.
 */
export function useEmailHistory(supabase: SupabaseClient, userId: string, tenantId: string) {
  return useQuery({
    queryKey: ['email-history', userId, tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_deliveries')
        .select(
          'id,tenant_id,user_id,email_queue_id,template,subject,recipient_email,resend_message_id,status,bounced_reason,sent_at,delivered_at,created_at',
        )
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as EmailDeliveryRow[];
    },
    enabled: Boolean(userId) && Boolean(tenantId),
    staleTime: 60_000,
  });
}

export function useUnsubscribe(supabase: SupabaseClient) {
  return useMutation({
    mutationFn: async (token: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        'content-type': 'application/json',
      };
      if (session?.access_token) {
        headers.authorization = `Bearer ${session.access_token}`;
      }
      const res = await fetch('/functions/v1/notifications/unsubscribe', {
        method: 'POST',
        headers,
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error(await res.text());
    },
  });
}
